import React from 'react';
import { cn } from './utils/cn';

/** Отступ края дорожки до центра «ручки» в крайних положениях (половина ручки ≈ 11px). */
const RAIL_INSET_PX = 11;

/**
 * Выбор размера бет/рейза между min и max (фишки, целые).
 * Явная дорожка, заливка до значения и видимая ручка; нативный range сверху для перетаскивания.
 */
export function PokerBetRaiseSlider({ min, max, value, onChange, className }) {
  const lo = Number(min);
  const hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;

  const v = Math.min(hi, Math.max(lo, Number(value) || lo));
  const t = (v - lo) / (hi - lo);
  const railSpan = `calc(100% - ${RAIL_INSET_PX * 2}px)`;
  const knobLeft = `calc(${RAIL_INSET_PX}px + ${railSpan} * ${t})`;
  const fillWidth = `max(0px, calc(${railSpan} * ${t}))`;

  return (
    <div className={cn('mx-auto w-full max-w-lg px-1 sm:px-0', className)}>
      <div
        className={cn(
          'rounded-2xl border border-white/[0.07] bg-gradient-to-b from-card/90 to-card/55 p-3.5 shadow-inner backdrop-blur-md',
          'ring-1 ring-white/[0.05] focus-within:ring-2 focus-within:ring-orange-400/40 focus-within:ring-offset-2 focus-within:ring-offset-background'
        )}
      >
        <div className="mb-3 flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Минимум</p>
            <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-muted-foreground/95 sm:text-base">
              {lo.toLocaleString('ru-RU')}
            </p>
          </div>
          <div className="shrink-0 text-center">
            <p className="text-[10px] font-medium uppercase tracking-wider text-orange-200/80">Сумма</p>
            <p className="mt-0.5 font-mono text-lg font-bold tabular-nums tracking-tight text-orange-50 sm:text-xl">
              {v.toLocaleString('ru-RU')}
              <span className="ml-1 text-xs font-semibold text-orange-200/90 sm:text-sm">SEKA</span>
            </p>
          </div>
          <div className="min-w-0 flex-1 text-right">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Максимум</p>
            <p className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-muted-foreground/95 sm:text-base">
              {hi.toLocaleString('ru-RU')}
            </p>
          </div>
        </div>

        <div className="px-0.5 pt-0.5">
          <div className="relative h-12 w-full select-none">
            {/* Фон шкалы */}
            <div
              className="pointer-events-none absolute left-[11px] right-[11px] top-1/2 h-3 -translate-y-1/2 rounded-full bg-slate-950/90 ring-1 ring-white/10 shadow-[inset_0_1px_4px_rgba(0,0,0,0.5)]"
              aria-hidden
            />
            {/* Оранжевая часть: от левого края дорожки до текущего значения */}
            <div
              className={cn(
                'pointer-events-none absolute top-1/2 h-3 -translate-y-1/2 rounded-full bg-gradient-to-r from-orange-600 via-orange-500 to-amber-400',
                'shadow-[0_0_16px_rgba(251,146,60,0.35)] transition-[width] duration-100 ease-out',
                t >= 0.999 ? 'rounded-full' : 'rounded-l-full rounded-r-sm'
              )}
              style={{
                left: RAIL_INSET_PX,
                width: fillWidth,
              }}
              aria-hidden
            />
            {/* Ручка */}
            <div
              className="pointer-events-none absolute top-1/2 z-[1] h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-gradient-to-b from-orange-50 to-orange-600 shadow-[0_3px_12px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.25)_inset] ring-2 ring-orange-400/30 transition-[left] duration-100 ease-out"
              style={{ left: knobLeft }}
              aria-hidden
            />
            <input
              type="range"
              min={lo}
              max={hi}
              step={1}
              value={v}
              onChange={(e) => onChange(Number(e.target.value))}
              className={cn(
                'absolute inset-0 z-[2] m-0 h-full w-full cursor-grab appearance-none bg-transparent p-0 opacity-0',
                'focus-visible:outline-none',
                '[&::-webkit-slider-runnable-track]:h-12 [&::-webkit-slider-runnable-track]:bg-transparent',
                '[&::-webkit-slider-thumb]:h-12 [&::-webkit-slider-thumb]:w-12 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-transparent',
                '[&::-moz-range-track]:h-12 [&::-moz-range-track]:bg-transparent',
                '[&::-moz-range-thumb]:h-12 [&::-moz-range-thumb]:w-12 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent',
                'active:cursor-grabbing'
              )}
              aria-label="Размер бет или рейза"
            />
          </div>
          <p className="mt-2 text-center text-[10px] text-muted-foreground/85">Двигайте ползунок по линии между минимумом и максимумом</p>
        </div>
      </div>
    </div>
  );
}
